// src/App.js
// UPDATED: Added push notifications for iOS + User Presence Tracking
// 🔄 STRATEGIC UPDATE: /football route now renders AiAssistant (Premium Singles discontinued)
// 🔥 IGNIS AI AGENT: /ignis public landing + /api docs for enterprise partners

import React from 'react';
import { Routes, Route } from 'react-router-dom';
import './App.css';

// 🔔 Push Notifications
import usePushNotifications from './hooks/usePushNotifications';
// 👥 User Presence Tracking (Web + iOS)
import usePresence from './hooks/usePresence';

// --- VIEWS IMPORTS ---
import Header from './Views/Header';
import TrustSection from './Views/TrustSection/TrustSection';
import TestimonialsSection from './Views/TestimonialsSection/TestimonialsSection';
import CryptoTrades from './Views/CryptoTrades/CryptoTrades';
import RiskManagement from './Views/RiskManagement/RiskManagement';
import Signup from './Views/Signup/Signup';
import Signin from './Views/Signin/Signin';
import ForgotPassword from './Views/ForgotPassword/ForgotPassword';
import Signout from './Views/Signout';
import AuthAction from './Views/AuthAction';
import PremiumHowItWorks from './Views/PremiumHowItWorks/PremiumHowItWorks';
import Pricing from './Views/Pricing/Pricing';
import Dashboard from './Views/Dashboard/Dashboard';
import AiAssistant from './Views/AiAssistant/AiAssistant';
import PredictionHistory from './Views/PredictionHistory/PredictionHistory';
import Results from './Views/Results/Results';
import Profile from './Views/Profile/Profile';
import PremiumHowToPlay from './Views/PremiumHowToPlay/PremiumHowToPlay';
import About from './Views/About/About';
import PrivacyPolicy from './Views/PrivacyPolicy/PrivacyPolicy';
import Legal from './Views/Legal/Legal';
import Support from './Views/Support/Support';
import Unsubscribe from './Views/Unsubscribe/Unsubscribe';

// 🔥 IGNIS AI AGENT
import IgnisLanding from './Views/IgnisLanding/IgnisLanding';
import IgnisAPIDocs from './Views/IgnisAPIDocs/IgnisAPIDocs';

// --- ADMIN IMPORTS ---
import AdminLayout from './Views/Admin/AdminLayout'; 
import AdminDashboard from './Views/Admin/AdminDashboard'; 
import FootballAdmin from './Views/Admin/FootballAdmin';
import AdminCrypto from './Views/AdminCrypto/AdminCrypto'; 
import AdminAnnouncements from './Views/Admin/AdminAnnouncements';
import AdminTestimonials from './Views/Admin/AdminTestimonials';
import AdminSupport from './Views/Admin/AdminSupport';
import PremiumCleanup from './Views/Admin//PremiumCleanup';
import AdminPresence from './Views/Admin/AdminPresence';

// --- SECURITY IMPORTS ---
import ProtectedRoute from './Views/ProtectedRoute';
import FAQ from './Views/FAQ/FAQ';
import Referral from './Views/Referral/Referral';
import ProfitGuide from './Views/ProfitGuide/ProfitGuide';


const App = () => {
  console.log('🚀 App.js loaded');
  // 🔔 Initialize push notifications (iOS only)
  usePushNotifications();
  // 👥 Track user presence (Web + iOS)
  usePresence();

  return (
    <div className="app">
      
    
      <Header/>

      <main>
        <Routes>
          {/* --- PUBLIC ROUTES --- */}
          <Route path="/" element={<><TrustSection/><TestimonialsSection/></>} />
          <Route path="/auth-action" element={<AuthAction />} />
          <Route path="/about" element={<About />} />
          <Route path="/how-it-works" element={<PremiumHowItWorks />} />
          <Route path="/pricing" element={<Pricing />} />
          <Route path="/legal" element={<Legal />} />
          <Route path="/privacy-policy" element={<PrivacyPolicy />} />
          <Route path="/premium/how-to-play" element={<PremiumHowToPlay />} />
          <Route path="/support" element={<Support />} />
          <Route path="/guide" element={<ProfitGuide />} />
          
          {/* 🔥 IGNIS AI AGENT — Public pages for SEO + marketing + enterprise */}
          <Route path="/ignis" element={<IgnisLanding />} />
          <Route path="/api" element={<IgnisAPIDocs />} />

          {/* Auth */}
          <Route path="/signin" element={<Signin/>} />
          <Route path="/signup" element={<Signup/>} />
          <Route path="/join/:code" element={<Signup />} />
          <Route path="/faq" element={<FAQ />} />
          <Route path="/forgot-password" element={<ForgotPassword/>} />
          <Route path="/signout" element={<Signout/>} />
          <Route path="/unsubscribe" element={<Unsubscribe />} />

          {/* --- PROTECTED ROUTES --- */}
          <Route path="/app" element={
            <ProtectedRoute>
              <Dashboard />
            </ProtectedRoute>
          } />

          <Route path="/profile" element={
            <ProtectedRoute>
              <Profile />
            </ProtectedRoute>
          } />

          {/* 🔄 UPDATED: /football now renders AiAssistant (Premium Singles discontinued) */}
          <Route path="/football" element={
            <ProtectedRoute>
              <AiAssistant />
            </ProtectedRoute>
          } />

          <Route path="/risk" element={
            <ProtectedRoute>
              <RiskManagement/>
            </ProtectedRoute>
          } />

          <Route path="/results" element={
            <ProtectedRoute>
              <Results />
            </ProtectedRoute>
          } />

          {/* 🟢 AI ASSISTANT */}
          <Route path="/ai-assistant" element={
            <ProtectedRoute>
              <AiAssistant />
            </ProtectedRoute>
          } />

          {/* 📊 PREDICTION HISTORY */}
          <Route path="/prediction-history" element={
            <ProtectedRoute>
              <PredictionHistory />
            </ProtectedRoute>
          } />

          <Route path="/referral" element={<Referral />} />
          <Route path="/join/:code" element={<Signup />} />

          {/* 🔥 IGNIS AI AGENT DASHBOARD — Any logged-in user can view; enrollment toggle handles access */}
          <Route path="/crypto" element={
            <ProtectedRoute>
              <CryptoTrades/>
            </ProtectedRoute>
          } />

          {/* --- ADMIN SUITE --- */}
          <Route path="/admin" element={<AdminLayout><AdminDashboard /></AdminLayout>} />
          <Route path="/admin/football" element={<AdminLayout><FootballAdmin /></AdminLayout>} />
          <Route path="/admin/crypto" element={<AdminLayout><AdminCrypto /></AdminLayout>} />
          <Route path="/admin/announcements" element={<AdminLayout><AdminAnnouncements /></AdminLayout>} />
          <Route path="/admin/support" element={<AdminLayout><AdminSupport /></AdminLayout>} />
          <Route path="/admin/testimonials" element={<AdminLayout><AdminTestimonials /></AdminLayout>} />
          <Route path="/admin/presence" element={<AdminLayout><AdminPresence /></AdminLayout>} />

          {/* 🔥 ADD THIS NEW ROUTE */}
          <Route path="/admin/cleanup" element={
            <ProtectedRoute adminOnly={true}>
              <PremiumCleanup />
            </ProtectedRoute>
          } />

        </Routes>
      </main>
    </div>
  );
};

export default App;